// TanStack Query hooks for the Clients module. Clients = organizations (the
// billable client entity) plus their nested client_contacts. Mirrors
// packages/api/src/routes/clients.ts. Sites/posts/post-orders live in
// queries/sites.ts; principals live in queries/principals.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClientContact,
  ClientContactResponse,
  ClientContactsListResponse,
  CreateClientContactInput,
  CreateOrganizationInput,
  Organization,
  OrganizationResponse,
  OrganizationStatus,
  OrganizationType,
  OrganizationsListResponse,
  UpdateOrganizationInput,
} from '@dsr/shared-types';
import { api } from '../client';

// =============================================================================
// Organizations
// =============================================================================

export interface OrganizationFilters {
  status?: OrganizationStatus;
  type?: OrganizationType;
  tag?: string;
  q?: string;
}

export function useOrganizations(
  filters: OrganizationFilters = {},
  options: { enabled?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.tag) params.set('tag', filters.tag);
  if (filters.q && filters.q.trim().length > 0) params.set('q', filters.q.trim());
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/clients?${qs}` : '/api/clients';
  return useQuery({
    queryKey: ['organizations', { ...filters }],
    enabled: options.enabled ?? true,
    queryFn: ({ signal }) => api.get<OrganizationsListResponse>(url, signal),
  });
}

export function useOrganization(id: number | null) {
  return useQuery({
    queryKey: ['organization', id],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<OrganizationResponse>(`/api/clients/${id}`, signal),
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrganizationInput) =>
      api.post<OrganizationResponse>('/api/clients', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export interface UpdateOrganizationVars extends UpdateOrganizationInput {
  id: number;
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateOrganizationVars) =>
      api.patch<OrganizationResponse>(`/api/clients/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      void qc.invalidateQueries({ queryKey: ['organization', vars.id] });
    },
  });
}

export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/clients/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

// =============================================================================
// Client contacts (nested under an organization)
// =============================================================================

export function useClientContacts(orgId: number | null) {
  return useQuery({
    queryKey: ['client-contacts', orgId],
    enabled: orgId !== null && orgId > 0,
    queryFn: ({ signal }) =>
      api.get<ClientContactsListResponse>(`/api/clients/${orgId}/contacts`, signal),
  });
}

export interface CreateContactVars extends CreateClientContactInput {
  orgId: number;
}

export function useCreateClientContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, ...input }: CreateContactVars) =>
      api.post<ClientContactResponse>(`/api/clients/${orgId}/contacts`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['client-contacts', vars.orgId] });
    },
  });
}

// Re-export the wire types callers most often need alongside the hooks.
export type { Organization, ClientContact };
